import { useCallback, useMemo } from "react";
import { Outlet, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../common/state/hooks";
import DateRangePicker, { DateRange } from "../../features/statsNew/components/DateRangePicker";
import {
  selectEnduserDateRangeAsDates,
  setEnduserDateRange,
  clearEnduserDateRange,
} from "../../features/enduserStats/state/enduserStatsSlice";
import "../../features/statsNew/components/statsNew.css";

export type EnduserStatsTab = "overview" | "parcours" | "comparateur";

const EnduserStatsLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Get date range from Redux
  const dateRange = useSelector(selectEnduserDateRangeAsDates);

  // Determine active tab based on current route
  const getActiveTab = (): EnduserStatsTab => {
    if (location.pathname.includes("/stats/comparateur")) return "comparateur";
    if (location.pathname.includes("/stats/parcours")) return "parcours";
    return "overview";
  };

  const activeTab = getActiveTab();

  // Handle date range change - convert to ISO strings for Redux
  const handleDateRangeChange = useCallback(
    (newDateRange: DateRange) => {
      dispatch(
        setEnduserDateRange({
          startDate: newDateRange.startDate?.toISOString() ?? null,
          endDate: newDateRange.endDate?.toISOString() ?? null,
        }),
      );
    },
    [dispatch],
  );

  // Clear date range handler
  const handleClearDateRange = useCallback(() => {
    dispatch(clearEnduserDateRange());
  }, [dispatch]);

  // Memoize the outlet context
  const outletContext = useMemo(
    () => ({ activeTab, dateRange, clearDateRange: handleClearDateRange }),
    [activeTab, dateRange, handleClearDateRange],
  );

  const tabs: { key: EnduserStatsTab; label: string; path: string }[] = [
    { key: "overview", label: t("enduserStats.tabs.overview"), path: "/stats" },
    { key: "parcours", label: t("enduserStats.tabs.parcours"), path: "/stats/parcours" },
    { key: "comparateur", label: t("enduserStats.tabs.comparateur"), path: "/stats/comparateur" },
  ];

  return (
    <div className="h-full flex flex-col bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-2">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
          <Link to="/" className="hover:text-gray-700">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
          </Link>
          <span>/</span>
          <span>{t("enduserStats.title")}</span>
        </div>

        <h1 className="text-xl font-bold text-gray-900 mb-2">{t("enduserStats.title")}</h1>

        {/* Navigation Tabs and Date Picker */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1">
            {tabs.map((tab) => (
              <Link
                key={tab.key}
                to={tab.path}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === tab.key
                    ? "text-green-600 border-b-2 border-green-500"
                    : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                }`}
              >
                {tab.label}
              </Link>
            ))}
          </div>

          {/* Date picker */}
          <DateRangePicker value={dateRange} onChange={handleDateRangeChange} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        <div className="flex-1 min-h-0">
          <Outlet context={outletContext} />
        </div>
      </div>
    </div>
  );
};

export default EnduserStatsLayout;
